/**
 * Mobile Attachment Capture Component (Day-15 Module 9)
 * 
 * Sources:
 * - Camera capture
 * - Photo library
 * - File picker
 * 
 * Image Processing:
 * - Resize max width 1600px
 * - JPEG compression
 * 
 * Upload States:
 * - pending
 * - uploading
 * - synced
 * - failed
 */

import React, { useState, useRef, useCallback } from 'react';
import { Camera, Image as ImageIcon, FileUp, X, RotateCcw, Check, AlertCircle, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { processImage, createThumbnail, formatFileSize, generateFilename, isImageFile, fileToDataUrl } from '@/lib/imageProcessing';
import { 
  storeAttachment, 
  deleteAttachment, 
  ATTACHMENT_STATUS, 
  ATTACHMENT_ENTITY_TYPES,
  getAttachmentStatusLabel,
  getAttachmentStatusColor,
} from '@/lib/offline/attachmentStorage';

// ─────────────────────────────────────────────────────────────────
// Status Badge Component
// ─────────────────────────────────────────────────────────────────

function AttachmentStatusBadge({ status, onRetry }) {
  const colorMap = {
    [ATTACHMENT_STATUS.PENDING]: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    [ATTACHMENT_STATUS.UPLOADING]: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    [ATTACHMENT_STATUS.SYNCED]: 'bg-green-500/10 text-green-600 border-green-500/30',
    [ATTACHMENT_STATUS.FAILED]: 'bg-red-500/10 text-red-600 border-red-500/30',
  };

  const iconMap = {
    [ATTACHMENT_STATUS.PENDING]: <Loader2 className="h-3 w-3 animate-spin" />,
    [ATTACHMENT_STATUS.UPLOADING]: <Loader2 className="h-3 w-3 animate-spin" />,
    [ATTACHMENT_STATUS.SYNCED]: <Check className="h-3 w-3" />,
    [ATTACHMENT_STATUS.FAILED]: <AlertCircle className="h-3 w-3" />,
  };

  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${colorMap[status]}`}>
      {iconMap[status]}
      <span>{getAttachmentStatusLabel(status)}</span>
      {status === ATTACHMENT_STATUS.FAILED && onRetry && (
        <button onClick={onRetry} className="ml-1 hover:underline">
          Retry
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Attachment Preview Component
// ─────────────────────────────────────────────────────────────────

function AttachmentPreview({ attachment, onRemove, onRetry }) {
  const [showPreview, setShowPreview] = useState(false);
  const isImage = attachment.mimeType?.startsWith('image/');

  return (
    <>
      <div 
        className="relative group rounded-lg border overflow-hidden bg-muted/50"
        data-testid={`attachment-preview-${attachment.id}`}
      >
        {isImage && attachment.thumbnailUrl ? (
          <img 
            src={attachment.thumbnailUrl} 
            alt={attachment.filename}
            className="w-full h-24 object-cover cursor-pointer"
            onClick={() => setShowPreview(true)}
          />
        ) : (
          <div 
            className="w-full h-24 flex items-center justify-center cursor-pointer"
            onClick={() => setShowPreview(true)}
          >
            <FileUp className="h-8 w-8 text-muted-foreground" />
          </div>
        )}
        
        {/* Overlay with info */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
        
        {/* Status badge */}
        <div className="absolute top-1 left-1">
          <AttachmentStatusBadge status={attachment.status} onRetry={onRetry} />
        </div>
        
        {/* Remove button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="absolute top-1 right-1 p-1 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`remove-attachment-${attachment.id}`}
        >
          <X className="h-3 w-3" />
        </button>
        
        {/* File info */}
        <div className="absolute bottom-0 left-0 right-0 p-2 text-white text-xs truncate">
          {formatFileSize(attachment.size)}
        </div>
      </div>

      {/* Full preview dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="truncate">{attachment.filename}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center">
            {isImage && attachment.dataUrl ? (
              <img 
                src={attachment.dataUrl} 
                alt={attachment.filename}
                className="max-h-[60vh] object-contain"
              />
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                <FileUp className="h-16 w-16 mx-auto mb-4" />
                <p>{attachment.filename}</p>
                <p className="text-sm">{formatFileSize(attachment.size)}</p>
              </div>
            )}
            <div className="mt-4 flex items-center gap-2">
              <AttachmentStatusBadge status={attachment.status} />
              <span className="text-sm text-muted-foreground">
                {attachment.mimeType}
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────

export default function MobileAttachmentCapture({
  entityType = ATTACHMENT_ENTITY_TYPES.DOCUMENT,
  entityId,
  queueItemId,
  maxAttachments = 5,
  acceptedTypes = 'image/*,application/pdf',
  onAttachmentsChange,
  initialAttachments = [],
  disabled = false,
}) {
  const [attachments, setAttachments] = useState(initialAttachments);
  const [processing, setProcessing] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Handle file selection (from any source)
  const handleFileSelect = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    if (attachments.length >= maxAttachments) {
      toast.error(`Maximum ${maxAttachments} attachments allowed`);
      return;
    }

    setProcessing(true);
    const newAttachments = [];

    for (const file of Array.from(files)) {
      if (attachments.length + newAttachments.length >= maxAttachments) {
        toast.warning(`Only ${maxAttachments} attachments allowed`);
        break;
      }

      try {
        let dataUrl;
        let thumbnailUrl;
        let processedSize = file.size;

        if (isImageFile(file)) {
          // Process image - resize and compress
          const processed = await processImage(file, {
            maxWidth: 1600,
            maxHeight: 1600,
            quality: 0.8,
          });
          dataUrl = processed.dataUrl;
          processedSize = processed.processedSize;

          // Create thumbnail
          const thumbnail = await createThumbnail(file, 200);
          thumbnailUrl = thumbnail.dataUrl;

          // Show compression info
          if (file.size > processed.processedSize) {
            toast.info(`Image compressed: ${formatFileSize(file.size)} → ${formatFileSize(processed.processedSize)}`);
          }
        } else {
          // Non-image file - just read as data URL
          dataUrl = await fileToDataUrl(file);
        }

        // Store attachment locally
        const filename = generateFilename(file.name, entityType.toLowerCase());
        const stored = await storeAttachment({
          filename,
          mimeType: file.type || 'application/octet-stream',
          size: processedSize,
          dataUrl,
          thumbnailUrl,
          entityType,
          entityId,
          queueItemId,
          metadata: {
            originalName: file.name,
            originalSize: file.size,
          },
        });

        newAttachments.push(stored);
      } catch (err) {
        console.error('Error processing file:', err);
        toast.error(`Failed to process ${file.name}`);
      }
    }

    if (newAttachments.length > 0) {
      const updated = [...attachments, ...newAttachments];
      setAttachments(updated);
      onAttachmentsChange?.(updated);
      toast.success(`${newAttachments.length} attachment(s) added`);
    }

    setProcessing(false);
  }, [attachments, maxAttachments, entityType, entityId, queueItemId, onAttachmentsChange]);

  // Handle camera capture
  const handleCameraCapture = () => {
    cameraInputRef.current?.click();
  };

  // Handle gallery selection
  const handleGallerySelect = () => {
    galleryInputRef.current?.click();
  };

  // Handle file picker
  const handleFilePicker = () => {
    fileInputRef.current?.click();
  };

  // Remove attachment
  const handleRemoveAttachment = async (id) => {
    try {
      await deleteAttachment(id);
      const updated = attachments.filter(a => a.id !== id);
      setAttachments(updated);
      onAttachmentsChange?.(updated);
      toast.success('Attachment removed');
    } catch (err) {
      toast.error('Failed to remove attachment');
    }
  };

  // Retry failed attachment
  const handleRetryAttachment = async (id) => {
    // This would be handled by the sync service
    toast.info('Will retry on next sync');
  };

  // Clear all attachments
  const handleClearAll = async () => {
    for (const attachment of attachments) {
      await deleteAttachment(attachment.id);
    }
    setAttachments([]);
    onAttachmentsChange?.([]);
    toast.success('All attachments removed');
  };

  return (
    <div className="space-y-4" data-testid="mobile-attachment-capture">
      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFileSelect(e.target.files)}
        data-testid="camera-input"
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFileSelect(e.target.files)}
        data-testid="gallery-input"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes}
        multiple
        className="hidden"
        onChange={(e) => handleFileSelect(e.target.files)}
        data-testid="file-input"
      />

      {/* Source buttons */}
      <div className="grid grid-cols-3 gap-2">
        <Button
          variant="outline"
          className="flex flex-col items-center gap-1 h-auto py-3"
          onClick={handleCameraCapture}
          disabled={disabled || processing || attachments.length >= maxAttachments}
          data-testid="capture-camera-btn"
        >
          <Camera className="h-5 w-5" />
          <span className="text-xs">Camera</span>
        </Button>
        <Button
          variant="outline"
          className="flex flex-col items-center gap-1 h-auto py-3"
          onClick={handleGallerySelect}
          disabled={disabled || processing || attachments.length >= maxAttachments}
          data-testid="capture-gallery-btn"
        >
          <ImageIcon className="h-5 w-5" />
          <span className="text-xs">Gallery</span>
        </Button>
        <Button
          variant="outline"
          className="flex flex-col items-center gap-1 h-auto py-3"
          onClick={handleFilePicker}
          disabled={disabled || processing || attachments.length >= maxAttachments}
          data-testid="capture-file-btn"
        >
          <FileUp className="h-5 w-5" />
          <span className="text-xs">Files</span>
        </Button>
      </div>

      {/* Processing indicator */}
      {processing && (
        <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Processing...</span>
        </div>
      )}

      {/* Attachments grid */}
      {attachments.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">
                Attachments ({attachments.length}/{maxAttachments})
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                onClick={handleClearAll}
                data-testid="clear-all-attachments"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Clear all
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {attachments.map((attachment) => (
                <AttachmentPreview
                  key={attachment.id}
                  attachment={attachment}
                  onRemove={() => handleRemoveAttachment(attachment.id)}
                  onRetry={() => handleRetryAttachment(attachment.id)}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Limit hint */}
      {attachments.length === 0 && !processing && (
        <p className="text-xs text-center text-muted-foreground">
          Add up to {maxAttachments} photos or documents
        </p>
      )}
    </div>
  );
}

// Export sub-components for reuse
export { AttachmentStatusBadge, AttachmentPreview };
